import { useEffect, useMemo, useState, type FC } from "react";

import { AgentPeek } from "@/components/brand";
import {
  panelAgentLabel,
  useAgentPanelStore,
  type PanelAgent
} from "@/features/agents/agentPanelStore";
import { useNotifications } from "@/features/notifications/NotificationsProvider";
import { cn } from "@/lib/utils";

const PANEL_SCOPES = new Set(["practice", "client"]);

/** Past the unfold and the two highlight sweeps over the words. */
export const CUE_SHOW_MS = 4500;
/** His duck under the bar when the panel opens (`agent-peek-duck`). */
export const CUE_DUCK_MS = 160;
/** How often the words come back while the panel is closed. */
export const CUE_REAPPEAR_MS = 30 * 1000;

type Props = {
  agent: PanelAgent;
  className?: string;
};

/**
 * "Ask Casey" and the waving robot at the bar's right end; it opens the agent
 * panel and shows a dot for an unread practice or client mention. The robot
 * stands on the bar's bottom edge. Opening the panel, he ducks under that
 * edge and the cue goes (he pops up in the panel's header instead); closing
 * it, he pops back up here.
 * The cue unrolls leftward on arrival, a highlight sweeps across the words
 * twice, then the words fold away. They unfold on hover and focus, and come
 * back with the sweep every 30 seconds.
 */
export const AssistantCue: FC<Props> = ({ agent, className }) => {
  const open = useAgentPanelStore((s) => s.open);
  const toggle = useAgentPanelStore((s) => s.toggle);
  const { unreadFor } = useNotifications();
  const unread = useMemo(
    () =>
      unreadFor({}).some((n) => {
        const kind = n.context?.scope_kind;
        return typeof kind === "string" && PANEL_SCOPES.has(kind);
      }),
    [unreadFor]
  );
  const [shown, setShown] = useState(true);
  useEffect(() => {
    if (!shown) return undefined;
    const t = window.setTimeout(() => setShown(false), CUE_SHOW_MS);
    return () => window.clearTimeout(t);
  }, [shown]);
  useEffect(() => {
    if (open) return undefined;
    const t = window.setInterval(() => {
      if (!document.hidden) setShown(true);
    }, CUE_REAPPEAR_MS);
    return () => window.clearInterval(t);
  }, [open]);
  // Opening the panel: he ducks under the bar, then the cue leaves.
  const [present, setPresent] = useState(!open);
  useEffect(() => {
    if (!open) {
      setPresent(true);
      return undefined;
    }
    const t = window.setTimeout(() => setPresent(false), CUE_DUCK_MS);
    return () => window.clearTimeout(t);
  }, [open]);
  if (!present) return null;
  const leaving = open;
  const label = panelAgentLabel(agent);

  return (
    <button
      type="button"
      data-testid="assistant-cue"
      data-shown={shown || undefined}
      aria-label={`Ask Casey, Your ${label}`}
      onClick={toggle}
      className={cn(
        "assistant-cue group relative flex shrink-0 items-center self-stretch rounded-md px-1 text-sm whitespace-nowrap outline-none [--cue-ink:var(--fg-2)] hover:[--cue-ink:var(--fg)] focus-visible:ring-2 focus-visible:ring-primary/40",
        className
      )}
    >
      <span
        aria-hidden
        data-testid="assistant-cue-text"
        className={cn(
          "assistant-cue-text mr-1.5 max-w-20 overflow-hidden transition-[max-width,margin,opacity] duration-300 ease-out motion-reduce:transition-none",
          shown && !leaving
            ? "assistant-cue-sweep"
            : "mr-0 max-w-0 opacity-0 group-hover:mr-1.5 group-hover:max-w-20 group-hover:opacity-100 group-focus-visible:mr-1.5 group-focus-visible:max-w-20 group-focus-visible:opacity-100"
        )}
      >
        Ask Casey
      </span>
      <span className="relative self-end">
        <span
          className="agent-peek-stage block [--peek-pop-delay:140ms]"
          data-leaving={leaving || undefined}
        >
          <AgentPeek className="h-6.5" />
        </span>
        {unread ? (
          <span
            aria-label="Unread agent mentions"
            data-testid="assistant-cue-unread"
            className="absolute -top-1 -right-0.5 size-2 rounded-full bg-primary ring-cutout ring-chrome"
          />
        ) : null}
      </span>
    </button>
  );
};
